import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';
import countryData from '../../utils/countory.json';
import { validateEmail, validatePassword, validateName, validatePhone, validateConfirmPassword, validateForm } from '../../utils/validation';
import { newRegistrationApi } from '../../services/authService';

function Register() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCountries, setFilteredCountries] = useState([]);
  const dropdownRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    letters: false,
    numbers: false,
    special: false
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCountries(countryData.countoryCode);
    } else {
      const filtered = countryData.countoryCode.filter(country => 
        country.country.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setFilteredCountries(countryData.countoryCode);
  }, []);

  const validationRules = {
    name: validateName,
    email: validateEmail,
    phone: validatePhone,
    password: validatePassword,
    confirmPassword: validateConfirmPassword
  };

  const checkPasswordRequirements = (password) => {
    return {
      length: password.length >= 8,
      letters: /[a-zA-Z]/.test(password),
      numbers: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
    const newErrors = { ...errors, [name]: undefined };
    
    if (name === 'password') {
      setPasswordRequirements(checkPasswordRequirements(value));
      if (newFormData.confirmPassword) {
        const confirmPasswordErrors = validateConfirmPassword(newFormData.confirmPassword, value);
        if (confirmPasswordErrors.length > 0) {
          newErrors.confirmPassword = confirmPasswordErrors;
        }
      }
    }
    
    if (name === 'confirmPassword' && newFormData.password) {
      const confirmPasswordErrors = validateConfirmPassword(value, newFormData.password);
      if (confirmPasswordErrors.length > 0) {
        newErrors.confirmPassword = confirmPasswordErrors;
      }
    }
    
    setFormData(newFormData);
    setErrors(newErrors);
  };

  const handleCountrySelect = (code) => {
    setSelectedCountryCode(code);
    setShowCountryDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm(formData, validationRules);
    if (validation.isValid) {
      setIsLoading(true);
      
      const organization_url = window.location.origin;
      const registrationData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        countryCode: selectedCountryCode,
        organization_url: organization_url
      };
      
      try {
        const response = await newRegistrationApi(registrationData);
        if (response.success) {
          sessionStorage.setItem('registrationData', JSON.stringify(registrationData));
          toast.success('Registration successful! Please verify OTP', {
            position: "top-center",
            autoClose: 2000,
            onClose: () => {
              setIsLoading(false);
              navigate('/auth/register-otp', { state: { email: formData.email } });
            }
          });
        }
      } catch (error) {
        if (error.response?.status === 4004 || error.response?.data?.code === 4004) {
          toast.error('Email already registered!', { position: "top-center", autoClose: 2000 });
        } else {
          toast.error(error.response?.data?.message || 'Registration failed. Please try again.', { position: "top-center", autoClose: 2000 });
        }
        setIsLoading(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };

  return (
    <div className="min-h-screen bg-light-background flex flex-col items-center justify-center p-4 sm:p-6">
      {/* Logo */}
      <div className="mb-6">
        <img src={getLogoByDomainAndAlt().logo} alt={getLogoByDomainAndAlt().alt} className="h-12 w-auto" />
      </div>
      
      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-font-primary mb-2">Create an Account</h2>
          <p className="text-font-secondary text-sm">Fill in your details to get started</p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-font-primary mb-1.5">Full Name</label>
            <div className="relative">
              <Icon icon="mdi:account-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type="text" 
                id="name" 
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe" 
                className={`w-full pl-10 pr-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${errors.name ? 'border-error' : 'border-input-border'}`}
                disabled={isLoading}
              />
            </div>
            {errors.name && <span className="text-error text-xs mt-1 block">{errors.name[0]}</span>}
          </div>
          
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-font-primary mb-1.5">Email Address</label>
            <div className="relative">
              <Icon icon="mdi:email-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type="text"
                id="email" 
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@email.com" 
                className={`w-full pl-10 pr-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${errors.email ? 'border-error' : 'border-input-border'}`}
                disabled={isLoading}
              />
            </div>
            {errors.email && <span className="text-error text-xs mt-1 block">{errors.email[0]}</span>}
          </div>
          
          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-font-primary mb-1.5">Phone Number</label>
            <div className="flex gap-2">
              {/* Country Code Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className="flex items-center gap-1 px-3 py-3 border border-input-border rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors min-w-[80px]"
                >
                  <span>{selectedCountryCode}</span>
                  <Icon icon="mdi:chevron-down" className="w-4 h-4 text-gray-400" />
                </button>

                {showCountryDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        placeholder="Search country..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-3 py-2 border border-input-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredCountries.map((country, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                          onClick={() => handleCountrySelect(country.code)}
                        >
                          <span className="text-font-primary">{country.country}</span>
                          <span className="text-font-secondary">{country.code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Phone Input */}
              <div className="relative flex-1">
                <Icon icon="mdi:phone-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input 
                  type="tel"
                  id="phone" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter 5-15 digits" 
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${errors.phone ? 'border-error' : 'border-input-border'}`}
                  disabled={isLoading}
                />
              </div>
            </div>
            {errors.phone && <span className="text-error text-xs mt-1 block">{errors.phone[0]}</span>}
          </div>
          
          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-font-primary mb-1.5">Password</label>
            <div className="relative">
              <Icon icon="mdi:lock-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type={showPassword ? "text" : "password"} 
                id="password" 
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••" 
                className={`w-full pl-10 pr-12 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${errors.password ? 'border-error' : 'border-input-border'}`}
                disabled={isLoading}
              />
              <button 
                type="button" 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                <Icon icon={showPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} className="w-5 h-5" />
              </button>
            </div>
            {errors.password && <span className="text-error text-xs mt-1 block">{errors.password[0]}</span>}
            
            {/* Password Requirements */}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${passwordRequirements.length ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <Icon icon={passwordRequirements.length ? "mdi:check" : "mdi:close"} className="w-3.5 h-3.5" />
                8+ Characters
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${passwordRequirements.letters ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <Icon icon={passwordRequirements.letters ? "mdi:check" : "mdi:close"} className="w-3.5 h-3.5" />
                Letters
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${passwordRequirements.numbers ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <Icon icon={passwordRequirements.numbers ? "mdi:check" : "mdi:close"} className="w-3.5 h-3.5" />
                Numbers
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${passwordRequirements.special ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                <Icon icon={passwordRequirements.special ? "mdi:check" : "mdi:close"} className="w-3.5 h-3.5" />
                Special Chars
              </span>
            </div>
          </div>
          
          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-font-primary mb-1.5">Confirm Password</label>
            <div className="relative">
              <Icon icon="mdi:lock-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type={showConfirmPassword ? "text" : "password"} 
                id="confirmPassword" 
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••" 
                className={`w-full pl-10 pr-12 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${errors.confirmPassword ? 'border-error' : 'border-input-border'}`}
                disabled={isLoading}
              />
              <button 
                type="button" 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
              >
                <Icon icon={showConfirmPassword ? "mdi:eye-off-outline" : "mdi:eye-outline"} className="w-5 h-5" />
              </button>
            </div>
            {errors.confirmPassword && <span className="text-error text-xs mt-1 block">{errors.confirmPassword[0]}</span>}
          </div>
          
          {/* Submit Button */}
          <button 
            type="submit" 
            className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
          
          {/* Login Link */}
          <p className="text-center text-sm text-font-secondary">
            Already have an account?{' '}
            <Link 
              to="/auth/login" 
              className={`text-primary hover:text-primary/80 font-medium ${isLoading ? 'pointer-events-none opacity-60' : ''}`}
            >
              Login
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default Register;
